import { Controller } from "@hotwired/stimulus"

const HEADING_PREFIX = '## '
const BULLET_PREFIX = '- '
const TABLE_SEPARATOR = '| --- | --- |'
const SHORTCUTS = { b: 'bold', i: 'italic' }

export default class extends Controller {
  static targets = ["content"]
  static values = { placeholders: Object }

  bold() {
    this.wrapSelection('**')
  }

  italic() {
    this.wrapSelection('_')
  }

  heading() {
    this.prefixLines(() => HEADING_PREFIX, { separateBlock: false })
  }

  bulletList() {
    this.prefixLines(() => BULLET_PREFIX, { separateBlock: true })
  }

  numberedList() {
    this.prefixLines((index) => `${index + 1}. `, { separateBlock: true })
  }

  link() {
    const { start, end, selected } = this.selection()
    const url = this.placeholdersValue.url
    const snippet = `[${selected}](${url})`
    const urlStart = start + snippet.lastIndexOf(url)

    this.replace(start, end, snippet, urlStart, urlStart + url.length)
  }

  table() {
    const column = this.placeholdersValue.column
    const lineStart = this.lineStart()
    const gap = this.followsText(lineStart) ? '\n' : ''
    const rows = [`| ${column} | ${column} |`, TABLE_SEPARATOR, '|  |  |']
    const trailingGap = this.textarea.value.slice(lineStart).trim() === '' ? '\n' : '\n\n'
    const columnStart = lineStart + gap.length + 2

    this.replace(lineStart, lineStart, `${gap}${rows.join('\n')}${trailingGap}`,
                 columnStart, columnStart + column.length)
  }

  shortcut(event) {
    if (!event.ctrlKey && !event.metaKey) return

    const method = SHORTCUTS[event.key.toLowerCase()]
    if (!method) return

    event.preventDefault()
    this[method]()
  }

  get textarea() {
    return this.contentTarget
  }

  selection() {
    const { selectionStart, selectionEnd, value } = this.textarea

    return {
      start: selectionStart,
      end: selectionEnd,
      selected: value.slice(selectionStart, selectionEnd) || this.placeholdersValue.text
    }
  }

  wrapSelection(marker) {
    const { start, end, selected } = this.selection()
    const wrappedStart = start + marker.length

    this.replace(start, end, `${marker}${selected}${marker}`, wrappedStart, wrappedStart + selected.length)
  }

  prefixLines(prefixFor, { separateBlock }) {
    const start = this.lineStart()
    const end = this.lineEnd()
    const lines = (this.textarea.value.slice(start, end) || this.placeholdersValue.text).split('\n')
    const prefixed = lines.map((line, index) => `${prefixFor(index)}${line}`).join('\n')
    const gap = separateBlock && this.followsText(start) ? '\n' : ''

    this.replace(start, end, `${gap}${prefixed}`, start + gap.length, start + gap.length + prefixed.length)
  }

  lineStart() {
    return this.textarea.value.lastIndexOf('\n', this.textarea.selectionStart - 1) + 1
  }

  lineEnd() {
    const next = this.textarea.value.indexOf('\n', this.textarea.selectionEnd)

    return next === -1 ? this.textarea.value.length : next
  }

  followsText(start) {
    if (start === 0) return false

    const preceding = this.textarea.value.slice(0, start - 1)

    return preceding.slice(preceding.lastIndexOf('\n') + 1).trim() !== ''
  }

  replace(start, end, text, selectionStart, selectionEnd) {
    this.textarea.focus()
    this.textarea.setSelectionRange(start, end)

    if (!this.insertWithUndoHistory(text)) {
      this.textarea.setRangeText(text, start, end, 'end')
      this.textarea.dispatchEvent(new Event('input', { bubbles: true }))
    }

    this.textarea.setSelectionRange(selectionStart, selectionEnd)
  }

  // execCommand is deprecated, but it is the only insertion a browser records in the textarea's
  // own undo stack, so ctrl+z reverts a toolbar edit the way it reverts typing. It replaces the
  // current selection and fires the input event itself.
  insertWithUndoHistory(text) {
    return typeof document.execCommand === 'function' && document.execCommand('insertText', false, text)
  }
}
